import React, { useCallback, useMemo, useState } from "react";

// ─── константы ────────────────────────────────────────────────────────────────

const DAYS = [
    { key: 1, label: "Понедельник", short: "Пн" },
    { key: 2, label: "Вторник",     short: "Вт" },
    { key: 3, label: "Среда",       short: "Ср" },
    { key: 4, label: "Четверг",     short: "Чт" },
    { key: 5, label: "Пятница",     short: "Пт" },
    { key: 6, label: "Суббота",     short: "Сб" },
    { key: 7, label: "Воскресенье", short: "Вс" },
];

function emptyWeek() {
    const w = {};
    DAYS.forEach(d => { w[d.key] = []; });
    return w;
}

function clone(obj) {
    return JSON.parse(JSON.stringify(obj ?? null));
}

function toMins(t) {
    if (!t) return 0;
    const [h, m] = String(t).split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
}

function normalizeIntervals(arr) {
    return [...(arr || [])]
        .filter(x => x?.start && x?.end)
        .sort((a, b) => toMins(a.start) - toMins(b.start));
}

// Понедельник недели для любой даты
function toMonday(dateStr) {
    const d = dateStr ? new Date(dateStr) : new Date();
    const day = d.getDay(); // 0=Sun, 1=Mon ...
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    return d.toISOString().slice(0, 10);
}

function addWeeks(dateStr, n) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + n * 7);
    return d.toISOString().slice(0, 10);
}

function weekLabel(dateFrom) {
    if (!dateFrom) return "Неделя";
    const d = new Date(dateFrom);
    const end = new Date(dateFrom);
    end.setDate(end.getDate() + 6);
    const fmt = (dt) => `${String(dt.getDate()).padStart(2, "0")}.${String(dt.getMonth() + 1).padStart(2, "0")}`;
    return `${fmt(d)}–${fmt(end)} ${end.getFullYear()}`;
}

// ─── DayRow — одна строка дня внутри недели ───────────────────────────────────

function DayRow({ dayDef, intervals, onChange }) {
    const enabled = intervals.length > 0;

    const toggle = (on) => onChange(on ? [{ start: "10:00", end: "18:00" }] : []);

    const addInterval = () => {
        const norm = normalizeIntervals(intervals);
        const last = norm[norm.length - 1];
        const start = last?.end ?? "10:00";
        const endMins = Math.min(23 * 60 + 59, toMins(start) + 120);
        const hh = String(Math.floor(endMins / 60)).padStart(2, "0");
        const mm = String(endMins % 60).padStart(2, "0");
        onChange(normalizeIntervals([...norm, { start, end: `${hh}:${mm}` }]));
    };

    const change = (idx, field, val) => {
        onChange(normalizeIntervals(intervals.map((it, i) => i === idx ? { ...it, [field]: val } : it)));
    };

    const remove = (idx) => {
        onChange(normalizeIntervals(intervals.filter((_, i) => i !== idx)));
    };

    return (
        <div className={`fse-day${enabled ? " is-on" : ""}`}>
            <div className="fse-day__head">
                <label className="b-switch fse-day__switch" title={enabled ? "Выходной" : "Рабочий"}>
                    <input type="checkbox" checked={enabled} onChange={e => toggle(e.target.checked)} />
                    <span className="b-switch__ui" aria-hidden="true" />
                </label>
                <span className="fse-day__name">
                    <span className="b-chip b-chip--sm">{dayDef.short}</span>
                    {dayDef.label}
                </span>
                {enabled && (
                    <span className="fse-day__summary">
                        {normalizeIntervals(intervals).map(x => `${x.start}–${x.end}`).join(", ")}
                    </span>
                )}
                {enabled && (
                    <button
                        type="button"
                        className="fse-day__add-btn"
                        onClick={addInterval}
                        title="Добавить интервал"
                    >+</button>
                )}
            </div>

            {enabled && (
                <div className="fse-day__intervals">
                    {normalizeIntervals(intervals).map((it, idx) => {
                        const invalid = toMins(it.start) >= toMins(it.end);
                        return (
                            <div key={idx} className={`fse-day__interval${invalid ? " is-err" : ""}`}>
                                <input
                                    className={`b-input b-input--sm${invalid ? " is-error" : ""}`}
                                    type="time"
                                    value={it.start}
                                    onChange={e => change(idx, "start", e.target.value)}
                                />
                                <span className="fse-day__dash">—</span>
                                <input
                                    className={`b-input b-input--sm${invalid ? " is-error" : ""}`}
                                    type="time"
                                    value={it.end}
                                    onChange={e => change(idx, "end", e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="b-icon-btn"
                                    onClick={() => remove(idx)}
                                    title="Удалить"
                                >×</button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── WeekBlock — один блок недели ─────────────────────────────────────────────

function WeekBlock({ entry, onChangeWeek, onRemove, onCopy, onPaste, canPaste, canRemove }) {
    const [open, setOpen] = useState(true);
    const [applyCount, setApplyCount] = useState(1);

    const changeDay = (dayKey, intervals) => {
        onChangeWeek({ ...entry, week: { ...entry.week, [dayKey]: intervals } });
    };

    const activeDays = DAYS.filter(d => (entry.week[d.key] || []).length > 0).length;

    return (
        <div className="fse-week">
            <div className="fse-week__head" onClick={() => setOpen(v => !v)}>
                <div className="fse-week__head-left">
                    <span className="fse-week__caret">{open ? "▾" : "▸"}</span>
                    <span className="fse-week__label">{weekLabel(entry.dateFrom)}</span>
                    <span className="fse-week__days-count">{activeDays} дн.</span>
                </div>
                <div className="fse-week__head-actions" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        className="b-schedule-editor__mini-btn"
                        title="Скопировать неделю"
                        onClick={() => onCopy(entry)}
                    >⎘</button>
                    <button
                        type="button"
                        className={`b-schedule-editor__mini-btn${canPaste ? "" : " is-disabled"}`}
                        title={canPaste ? "Вставить из скопированной" : "Сначала скопируйте неделю"}
                        disabled={!canPaste}
                        onClick={() => onPaste(entry.dateFrom)}
                    >⎀</button>
                    {canRemove && (
                        <button
                            type="button"
                            className="b-icon-btn"
                            title="Удалить неделю"
                            onClick={() => onRemove(entry.dateFrom)}
                        >×</button>
                    )}
                </div>
            </div>

            {open && (
                <div className="fse-week__body">
                    <div className="fse-week__days">
                        {DAYS.map(d => (
                            <DayRow
                                key={d.key}
                                dayDef={d}
                                intervals={entry.week[d.key] || []}
                                onChange={intervals => changeDay(d.key, intervals)}
                            />
                        ))}
                    </div>

                    <div className="fse-week__apply-row">
                        <span className="fse-week__apply-label">Скопировать на следующие</span>
                        <input
                            className="b-input fse-week__apply-count"
                            type="number"
                            min={1}
                            max={52}
                            value={applyCount}
                            onChange={e => setApplyCount(Math.max(1, Math.min(52, parseInt(e.target.value) || 1)))}
                            onClick={e => e.stopPropagation()}
                        />
                        <span className="fse-week__apply-label">нед.</span>
                        <button
                            type="button"
                            className="b-btn b-btn--transparent b-btn--sm"
                            onClick={e => { e.stopPropagation(); onCopy(entry, applyCount); }}
                        >Применить</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── FlexibleScheduleEditor — главный компонент ───────────────────────────────

/**
 * Props:
 *   value:    { slotMinutes: number, flexible: [{ dateFrom: string, week: {1:[...], ...} }] }
 *   onChange: (value) => void   — вызывается при каждом изменении
 *   onSave:   (value) => void   — вызывается при нажатии «Сохранить»
 *   saving:   boolean
 */
export default function FlexibleScheduleEditor({ value, onChange, onSave, saving }) {
    const slotMinutes = value?.slotMinutes ?? 50;
    const flexEntries = useMemo(() => {
        const arr = Array.isArray(value?.flexible) ? value.flexible : [];
        return arr.slice().sort((a, b) => String(a.dateFrom).localeCompare(String(b.dateFrom)));
    }, [value?.flexible]);

    const [clipboard, setClipboard] = useState(null); // { week }

    // Обновить список недель
    const updateFlex = useCallback((nextEntries) => {
        onChange?.({ ...value, slotMinutes, flexible: nextEntries });
    }, [value, slotMinutes, onChange]);

    // Добавить следующую неделю после последней
    const addWeek = () => {
        const existing = flexEntries.map(e => e.dateFrom);
        let base = toMonday(new Date().toISOString().slice(0, 10));
        // найти первую неделю, которой ещё нет
        while (existing.includes(base)) {
            base = addWeeks(base, 1);
        }
        updateFlex([...flexEntries, { dateFrom: base, week: emptyWeek() }]);
    };

    const removeWeek = (df) => updateFlex(flexEntries.filter(e => e.dateFrom !== df));

    const changeWeek = (entry) => updateFlex(flexEntries.map(e => e.dateFrom === entry.dateFrom ? entry : e));

    // Копировать неделю (и опционально применить к следующим N)
    const copyWeek = (entry, applyCount = 0) => {
        setClipboard({ week: clone(entry.week) });
        if (applyCount > 0) {
            // Применяем к applyCount следующих недель
            const base = entry.dateFrom;
            let next = [...flexEntries];
            for (let i = 1; i <= applyCount; i++) {
                const df = addWeeks(base, i);
                const exists = next.find(e => e.dateFrom === df);
                if (exists) {
                    next = next.map(e => e.dateFrom === df ? { ...e, week: clone(entry.week) } : e);
                } else {
                    next = [...next, { dateFrom: df, week: clone(entry.week) }];
                }
            }
            next.sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));
            updateFlex(next);
        }
    };

    const pasteWeek = (df) => {
        if (!clipboard) return;
        updateFlex(flexEntries.map(e => e.dateFrom === df ? { ...e, week: clone(clipboard.week) } : e));
    };

    const save = async () => {
        if (typeof onSave === "function") {
            await onSave({ ...value, slotMinutes, flexible: flexEntries });
        }
    };

    return (
        <div className="fse">
            <div className="b-schedule-editor__card">
                <div className="b-schedule-editor__title">Гибкий график</div>
                <div className="b-schedule-editor__subtitle">
                    Задайте разное расписание на каждую неделю. Если для недели расписание не задано — используется фиксированный шаблон.
                </div>

                <div className="b-schedule-editor__row">
                    <label className="b-field">
                        <div className="b-field__label">Длительность сессии (мин)</div>
                        <input
                            className="b-input"
                            type="number"
                            min={15}
                            step={5}
                            value={slotMinutes}
                            onChange={e => onChange?.({ ...value, slotMinutes: parseInt(e.target.value || "0") || 50 })}
                        />
                    </label>
                </div>

                <div className="fse__weeks">
                    {flexEntries.length === 0 && (
                        <div className="fse__empty">
                            Нет недель. Добавьте первую — она будет иметь приоритет над фиксированным графиком на свои даты.
                        </div>
                    )}
                    {flexEntries.map(entry => (
                        <WeekBlock
                            key={entry.dateFrom}
                            entry={entry}
                            onChangeWeek={changeWeek}
                            onRemove={removeWeek}
                            onCopy={copyWeek}
                            onPaste={pasteWeek}
                            canPaste={!!clipboard}
                            canRemove={true}
                        />
                    ))}
                </div>

                <button
                    type="button"
                    className="fse__add-week-btn"
                    onClick={addWeek}
                >
                    + Добавить неделю
                </button>

                <div className="b-schedule-editor__foot">
                    <div className="b-schedule-editor__meta">Недель: {flexEntries.length}</div>
                    <button
                        type="button"
                        className={`b-btn${saving ? " is-loading" : ""}`}
                        onClick={save}
                        disabled={saving}
                        aria-busy={saving}
                    >
                        {saving ? <span className="b-btn__spinner" aria-hidden="true" /> : null}
                        {saving ? "Сохранение…" : "Сохранить гибкий график"}
                    </button>
                </div>
            </div>
        </div>
    );
}
